import { CASH } from '@makemydeal/dr-platform-types';
import type { ValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationEngine/types';
import { customerValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/customerValidationConfig';
import { accessoryValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/accessoryValidationConfig';
import { lenderValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/lenderValidationConfig';
import {
    vehicleInsuranceContractValidationConfig,
    contractApiErroredVehicleInsuranceValidationConfig
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleInsuranceValidationConfig';
import {
    providerValidationConfig,
    contractApiErroredVppValidationConfig
} from '@makemydeal/dr-shared-ui-utils/validationConfigs/vehicleProtectionProductsValidationConfig';
import { CONTRACT_API_ERRORED_VPP, CONTRACT_API_ERRORED_VEHICLE_INSURANCE } from '@makemydeal/dr-shared-types';
import { isPrincipalDriverNameRequired, PRINCIPAL_DRIVER_NAME_MAX_LENGTH } from './contractFieldErrorConfigs';
import { tradeInValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/tradeInValidationConfig';
import { cancellationTermsValidationConfig } from '@makemydeal/dr-shared-ui-utils/validationConfigs/cancellationTermsValidationConfig';

// Regex constant reused across county validations
const ELECTRIC = 'Electric';

// Vehicle nested group
const vehicleGroupConfig: ValidationConfig = {
    bookoutOrInvoice: {
        rules: [
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: {
                    evaluator: (values: any) => {
                        const input = values || {};
                        return Boolean(input.invoiceAmount);
                    }
                },
                messages: 'Invoice is required.',
                conditions: { includeOfferTypes: [CASH] }
            },
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: {
                    evaluator: (values: any) => {
                        const input = values || {};
                        const isBookoutFieldsValid = Boolean(input.bookoutSource && input.bookoutValue);
                        return Boolean(isBookoutFieldsValid || input.invoiceAmount);
                    }
                },
                messages: 'Enter the invoice amount or use bookout value + source',
                conditions: { excludeOfferTypes: [CASH] }
            }
        ]
    },
    cylinders: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Number of Cylinders is required.',
                conditions: { applicableStates: ['NV', 'AZ'], excludeFuelTypes: [ELECTRIC] }
            }
        ]
    },
    odometerMileageType: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Actual Odometer Mileage is required.',
                conditions: { applicableStates: ['OH'] }
            }
        ]
    },
    vehicleUse: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Primary Use is required.'
            }
        ]
    },
    principalDriverName: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Principal Driver Name is required.',
                conditions: {
                    when: (ctx: any, _value: any, scope: any) =>
                        isPrincipalDriverNameRequired({
                            vehicleUse: scope?.vehicleUse,
                            offerType: ctx?.offerType,
                            lenderName: ctx?.values?.lender?.name
                        })
                }
            },
            {
                type: 'MAX_LENGTH',
                flow: 'contract',
                params: { max: PRINCIPAL_DRIVER_NAME_MAX_LENGTH },
                messages: `Principal Driver Name cannot be longer than ${PRINCIPAL_DRIVER_NAME_MAX_LENGTH} characters.`
            }
        ]
    }
};

// The new entity-separated contract field config
export const contractFieldConfig: ValidationConfig = {
    // Use customerValidationConfig grouped with dynamic error prefixes
    buyer: { $group: customerValidationConfig, errorPrefix: 'Applicant' },
    coBuyer: { $group: customerValidationConfig, errorPrefix: 'Co-Applicant' },
    vehicle: { $group: vehicleGroupConfig },
    // A collection: several GAP products can be selected, and each is validated and attributed on its own.
    gap: { $collection: providerValidationConfig, errorPrefix: 'GAP After Market ' },
    [CONTRACT_API_ERRORED_VPP]: { $collection: contractApiErroredVppValidationConfig },
    vehicleInsurance: { $group: vehicleInsuranceContractValidationConfig },
    [CONTRACT_API_ERRORED_VEHICLE_INSURANCE]: { $group: contractApiErroredVehicleInsuranceValidationConfig },
    lender: { $group: lenderValidationConfig },
    accessories: {
        $collection: accessoryValidationConfig,
        errorPrefix: 'Accessory'
    },
    tradeIn: { $group: tradeInValidationConfig, errorPrefix: 'Trade In ' },
    tradeIns: { $collection: tradeInValidationConfig, errorPrefix: 'Trade' },
    cancellationTerms: { $group: cancellationTermsValidationConfig },
    other: {}
};
